import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import { socialApi } from '@/services/socialApi';
import type { AppDispatch } from '@/store';

import { nuovoIdEvento, segnaRiletture } from './idEvento';
import { leggiEventoPresenza, type EventoPresenza } from './liveMatch';
import { tagPerEvento, type TagTempoReale } from './tempoReale';

type Dispatch = ThunkDispatch<unknown, unknown, UnknownAction>;
type TagEvento = TagTempoReale | { type: 'DmMessages'; id: string };
type StatoApi = { [socialApi.reducerPath]?: { subscriptions?: Record<string, Record<string, unknown> | undefined> } };

/**
 * Un evento del canale personale → le liste da rileggere, e SOLO le loro riletture portano l'id
 * `ev-<type>-…` (v. `idEvento.ts`). Si segnano solo gli endpoint che hanno una sottoscrizione viva: RTK
 * rilegge solo quelli, e un id segnato su una lista non montata finirebbe sul primo montaggio.
 */
export function rileggiPerEvento(data: unknown, dispatch: Dispatch, getState: () => unknown): void {
  // `type: 'presence'` (contratto r38 §1-bis) non rilegge una lista: porta lo stato giusto dentro l'evento, e
  // lo si scrive nella cache di QUELL'amico. Nessun id d'evento: non c'e' una rilettura da riconoscere.
  const presenza = leggiEventoPresenza(data);
  if (presenza) {
    // `Dispatch` qui e' quello senza stato tipizzato; `updateQueryData` vuole quello dello store (che e' il dispatch vero).
    applicaPresenza(presenza, dispatch as unknown as AppDispatch);
    return;
  }
  const tag = tagPerEvento(data);
  if (tag.length === 0) return;
  rileggiTagPerEvento(tag, String((data as { type?: unknown }).type), dispatch, getState);
}

/** Lo stesso, per tag gia' scelti: `erased` sul canale `dm:<id>` rilegge `{ DmMessages, id }`. */
export function rileggiTagPerEvento(
  tag: readonly TagEvento[],
  type: string,
  dispatch: Dispatch,
  getState: () => unknown,
): void {
  const stato = getState() as StatoApi;
  const sottoscrizioni = stato[socialApi.reducerPath]?.subscriptions ?? {};
  const endpoint = socialApi.util
    .selectInvalidatedBy(getState() as never, [...tag])
    .filter((q) => Object.keys(sottoscrizioni[q.queryCacheKey] ?? {}).length > 0)
    .map((q) => q.endpointName);
  if (endpoint.length > 0) segnaRiletture(endpoint, nuovoIdEvento(type));
  dispatch(socialApi.util.invalidateTags([...tag]));
}

/** A ogni sottoscrizione riuscita e al rientro: si rilegge tutto, SENZA id d'evento (non l'ha causato un evento). */
export function rileggiTutto(dispatch: Dispatch, tag: readonly TagEvento[]): void {
  dispatch(socialApi.util.invalidateTags([...tag]));
}

/**
 * Un evento `presence` → la cache della presenza di QUELL'amico, per id e nel feed degli online.
 * `liveMatch: null` (o assente) spegne «sta giocando». Lo `status` si scrive solo se e' uno che la TMA conosce.
 * ⚠️ `updateQueryData` non fa niente su una voce di cache che non esiste: l'amico non ancora letto prende il
 * valore giusto dalla sua prima lettura, e il feed NON guadagna righe da un evento (mai inventare un amico).
 * Il canale non ha storia: un evento perso lo ripara la rilettura della pagina Amici (R7, al massimo 60 s).
 */
export function applicaPresenza(e: EventoPresenza, dispatch: AppDispatch): void {
  dispatch(
    socialApi.util.updateQueryData('getUserPresence', e.userId, (draft) => {
      if (!draft?.presence) return;
      if (e.status) draft.presence.status = e.status;
      draft.presence.liveMatch = e.liveMatch;
    }),
  );
  dispatch(
    socialApi.util.updateQueryData('getOnlineFriendsFeed', undefined, (draft) => {
      const voce = draft?.feed?.find((f) => f.userId === e.userId);
      if (!voce) return;
      if (e.status) voce.status = e.status;
      voce.liveMatch = e.liveMatch;
    }),
  );
}
